import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';

import { Box } from '@mui/material';

import Typography from '../../typography/typography';
import DatePicker from '../../date-picker/date-picker';
import DateTimePicker from '../../date-time-picker/date-time-picker';
import TextField from '../../text-field/text-field';
import UploadMedia from '../../upload-media/upload-media';

import * as yup from 'yup';
import _ from 'lodash';
import dayjs from 'dayjs';
import PhoneInput from '../../phone-input/phone-input';
import Checkbox from '../../checkbox/checkbox';
import SearchableSelect from '../../searchable-select';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

type ValuesCache = Record<string, any>;

interface IInlineEditFields {
	cell?: any;
	column: any;
	row: any;
	table?: any;
	col: any;
	callback?: (value: any, column: any, rowId: any) => any;
	errors: Record<string, string>;
	validationSchema: any;
	setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	onChangeRowField?: (
		event: any,
		type: string,
		row: any,
		col: any,
		selectedData: any
	) => any;
	getSelectedData?: (fieldName: string, selectedRow: any) => any;
}

/* ------------------------------------------------------------------ *
 * Module level constants and helpers (allocated once, not per render)
 * ------------------------------------------------------------------ */

const EMPTY_EDIT_PROPERTIES = Object.freeze({}) as Record<string, any>;

/**
 * Marks a `_valuesCache` object as already normalised by this component.
 * A Symbol key is invisible to Object.keys / JSON.stringify / lodash, so it
 * never leaks into the payload sent to the API, yet it survives the object
 * spreads we do on every change. `Symbol.for` keeps it stable across module
 * federation remotes that each get their own copy of this bundle.
 */
const CACHE_NORMALISED = Symbol.for('erpsquad.inlineEdit.cacheNormalised');

const isCacheNormalised = (cache: any): boolean =>
	Boolean(cache && cache[CACHE_NORMALISED]);

const markCacheNormalised = (cache: ValuesCache): ValuesCache => {
	(cache as any)[CACHE_NORMALISED] = true;
	return cache;
};

const isBlank = (value: any): boolean =>
	value === undefined || value === null || value === '';

const shallowEqual = (a: ValuesCache = {}, b: ValuesCache = {}): boolean => {
	if (a === b) return true;
	const aKeys = Object.keys(a);
	const bKeys = Object.keys(b);
	if (aKeys.length !== bKeys.length) return false;
	return aKeys.every((key) => Object.is(a[key], b[key]));
};

/* ------------------------------------------------------------------ *
 * Row scoped pub/sub
 * Replaces the global window CustomEvent bus. Only the cells belonging to
 * the same row are woken up, instead of every editing cell on the page.
 * ------------------------------------------------------------------ */

type CacheListener = (cache: ValuesCache) => void;

const rowSubscribers = new Map<string, Set<CacheListener>>();

const subscribeToRow = (rowId: string, listener: CacheListener) => {
	let listeners = rowSubscribers.get(rowId);
	if (!listeners) {
		listeners = new Set();
		rowSubscribers.set(rowId, listeners);
	}
	listeners.add(listener);

	return () => {
		listeners?.delete(listener);
		if (listeners && listeners.size === 0) rowSubscribers.delete(rowId);
	};
};

const publishRowCache = (rowId: string, cache: ValuesCache) => {
	rowSubscribers.get(rowId)?.forEach((listener) => listener(cache));
};

/* ------------------------------------------------------------------ *
 * Value resolution
 * ------------------------------------------------------------------ */

/**
 * Resolves the value a field should start with, straight from `row.original`.
 *
 * THE FIX: when a column declares a `valueKey` that differs from its own
 * accessor id, the accessor holds the human readable label (`item` -> "Steel
 * Coil") while the bound value lives at the `valueKey` (`item_id` -> 42).
 * In that situation we must NEVER fall back to the accessor, otherwise the
 * label ends up in the payload as if it were the id. Date variants are the
 * one exception, where the accessor legitimately carries a date string.
 */
const readInitialValue = (mrtColumn: any, row: any): any => {
	const columnDef = mrtColumn?.columnDef ?? {};
	const columnId = mrtColumn?.id;
	const {
		valueKey,
		is_multiple: isMultiple,
		defaultValue
	} = (columnDef.editProperties ?? EMPTY_EDIT_PROPERTIES) as Record<string, any>;

	// Per cell variant. The previous implementation leaked the host cell's
	// variant into every other column's resolution.
	const variant = columnDef.editVariant;

	const accessorValue = row?.original?.[columnId];
	const boundValue = valueKey ? _.get(row?.original, valueKey) : accessorValue;

	// Fallback to the accessor only when it cannot be a label.
	const accessorIsSafeFallback = !valueKey || valueKey === columnId;
	const rawValue =
		boundValue !== undefined
			? boundValue
			: accessorIsSafeFallback
				? accessorValue
				: undefined;

	if (variant === 'select' && isMultiple) {
		if (isBlank(boundValue)) return defaultValue ?? [];
		return Array.isArray(boundValue) ? boundValue : [boundValue];
	}

	if (variant === 'date' || variant === 'date_time') {
		const dateSource = boundValue !== undefined ? boundValue : accessorValue;
		if (isBlank(dateSource)) return undefined;
		return dayjs(dateSource).isValid() ? dayjs(dateSource) : undefined;
	}

	if (variant === 'select') {
		return isBlank(boundValue) ? defaultValue ?? '' : boundValue;
	}

	if (variant === 'checkbox') {
		return isBlank(rawValue) ? Boolean(defaultValue) : Boolean(rawValue);
	}

	return isBlank(rawValue) && defaultValue !== undefined ? defaultValue : rawValue;
};

/** Builds the normalised cache for every editable cell of the row, once. */
const buildInitialCache = (row: any): ValuesCache => {
	const cache: ValuesCache = {};

	row.getAllCells().forEach((cell: any) => {
		const enableEditing = cell.column.columnDef?.enableEditing;
		if (enableEditing === undefined || enableEditing === true) {
			cache[cell.column.id] = readInitialValue(cell.column, row);
		}
	});

	return markCacheNormalised(cache);
};

/** Normalises the many shapes the child components emit into a raw value. */
const extractValue = (event: any, type?: string): any => {
	switch (type) {
		case 'date':
		case 'file':
		case 'image':
		case 'phone':
			return event;
		case 'checkbox':
			return event?.target?.checked;
		default:
			return event?.target ? event.target.value : event;
	}
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const InlineEditFields: React.FC<IInlineEditFields> = ({
	column,
	row,
	col,
	errors,
	validationSchema,
	setValidationErrors,
	onChangeRowField,
	getSelectedData
}) => {
	const [values, setValues] = useState<ValuesCache>(() => row?._valuesCache ?? {});
	const [customFilter, setCustomFilter] = useState<any>(null);

	const editVariant = col?.editVariant;
	// Stable reference: `|| {}` allocated a fresh object on every render, which
	// invalidated every hook that depended on it.
	const fieldProperties: Record<string, any> =
		column.columnDef?.editProperties ?? EMPTY_EDIT_PROPERTIES;

	const columnId = column.id;
	const rowId = row.id;

	/* -------------------------------------------------------------- *
	 * 1. Cross cell synchronisation (row scoped)
	 * -------------------------------------------------------------- */
	useEffect(
		() =>
			subscribeToRow(rowId, (cache) => {
				// Bail out when nothing this cell can observe actually moved.
				// The cell reads its own value, but `disable()` and
				// `customeFilter()` receive the whole cache, so a shallow
				// compare of the cache is the correct granularity here.
				setValues((prev) => (shallowEqual(prev, cache) ? prev : cache));
			}),
		[rowId]
	);

	/* -------------------------------------------------------------- *
	 * 2. One time cache normalisation, per row instead of per cell
	 * -------------------------------------------------------------- */
	useEffect(() => {
		const existing = row._valuesCache;

		if (isCacheNormalised(existing)) {
			// A sibling cell already normalised this row, just adopt it.
			setValues(existing);
			return;
		}

		const seeded = buildInitialCache(row);
		row._valuesCache = seeded;
		setValues(seeded);
		publishRowCache(rowId, seeded);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowId]);

	/* -------------------------------------------------------------- *
	 * 3. Custom filter resolution (state, not a ref, so the select
	 *    actually re-renders once the async filter resolves)
	 * -------------------------------------------------------------- */
	const rawCustomFilter = fieldProperties.customeFilter;

	useEffect(() => {
		if (editVariant !== 'select' || rawCustomFilter === undefined) return;

		if (typeof rawCustomFilter !== 'function') {
			setCustomFilter(rawCustomFilter ?? null);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const filter = await rawCustomFilter(row, row._valuesCache);
				if (!cancelled) setCustomFilter(filter ?? null);
			} catch {
				if (!cancelled) setCustomFilter(null);
			}
		})();

		return () => {
			cancelled = true;
		};
		// `values` is the dependency that represents "some field in this row
		// changed", which is what a dependent filter reacts to.
	}, [rawCustomFilter, editVariant, row, values]);

	/* -------------------------------------------------------------- *
	 * 4. Derived state
	 * -------------------------------------------------------------- */

	const isFieldDisabled = useMemo(() => {
		const { disable } = fieldProperties;
		if (typeof disable === 'function') {
			return Boolean(disable(row, row._valuesCache));
		}
		return Boolean(disable);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldProperties, row, values]);

	const shouldAutoFocus = useMemo(() => {
		const firstEditable = row
			.getAllCells()
			.map((cell: any) => cell.column.columnDef)
			.find(
				(def: any) =>
					def?.enableEditing === undefined || def?.enableEditing === true
			);
		return firstEditable?.accessorKey === columnId;
	}, [row, columnId]);

	// Correct id based default, shared by the field and the initial paint,
	// so the inputs are never uncontrolled on the very first render.
	const columnDefaultValue = useMemo(
		() => readInitialValue(column, row),
		[column, row]
	);

	const currentValue =
		values?.[columnId] !== undefined ? values[columnId] : columnDefaultValue;

	const dateValue = useMemo(() => {
		if (isBlank(currentValue)) return undefined;
		return dayjs(currentValue).isValid() ? dayjs(currentValue) : undefined;
	}, [currentValue]);

	const fieldError = errors?.[columnId];

	/* -------------------------------------------------------------- *
	 * 5. Validation
	 * -------------------------------------------------------------- */
	const validate = useCallback(
		async (value: any) => {
			if (!validationSchema) return;

			try {
				await validationSchema.validateAt(
					columnId,
					{ [columnId]: value },
					{ abortEarly: false }
				);

				// Functional update: two sibling cells validating in the same
				// tick no longer overwrite each other's errors.
				setValidationErrors((prev) => {
					if (!prev || prev[columnId] === undefined) return prev ?? {};
					const next = { ...prev };
					delete next[columnId];
					return next;
				});
			} catch (err) {
				if (err instanceof yup.ValidationError) {
					const fieldErrors: Record<string, string> = {};
					err.inner.forEach((error) => {
						if (error.path) fieldErrors[error.path] = error.message;
					});
					if (!err.inner.length && err.path) {
						fieldErrors[err.path] = err.message;
					}
					setValidationErrors((prev) => ({ ...(prev ?? {}), ...fieldErrors }));
				}
			}
		},
		[columnId, setValidationErrors, validationSchema]
	);

	/* -------------------------------------------------------------- *
	 * 6. Change handling
	 * -------------------------------------------------------------- */
	const handleChange = useCallback(
		async (event: any, type?: string, selectedData?: any) => {
			const value = extractValue(event, type);

			// Commit synchronously first so a slow async `onChangeRowField`
			// can never swallow a keystroke.
			const optimistic = { ...(row._valuesCache ?? {}), [columnId]: value };
			row._valuesCache = optimistic;
			setValues(optimistic);
			publishRowCache(rowId, optimistic);

			if (onChangeRowField) {
				const patch = await onChangeRowField(event, type as string, row, col, selectedData);
				if (patch) {
					// The edited column keeps precedence over the patch, matching
					// the original ordering.
					const merged = { ...row._valuesCache, ...patch, [columnId]: value };
					row._valuesCache = merged;
					setValues(merged);
					publishRowCache(rowId, merged);
				}
			}

			await validate(value);
		},
		[col, columnId, onChangeRowField, row, rowId, validate]
	);

	const resolveDateBoundary = useCallback(
		(boundary: any) => (typeof boundary === 'function' ? boundary(row) : boundary),
		[row]
	);

	/* -------------------------------------------------------------- *
	 * 7. Render
	 * -------------------------------------------------------------- */

	if (!editVariant || editVariant === 'number') {
		return (
			<TextField
				defaultValue={columnDefaultValue}
				onChange={(e: any) => handleChange(e, editVariant)}
				value={currentValue ?? ''}
				autoFocus={shouldAutoFocus}
				type={editVariant || 'text'}
				error={Boolean(fieldError)}
				helperText={fieldError}
				placeholder={fieldProperties?.placeholder || ''}
				name={fieldProperties?.field_name || ''}
				disabled={isFieldDisabled}
			/>
		);
	}

	if (editVariant === 'select') {
		const isMultiple = Boolean(fieldProperties?.is_multiple);
		const selectValue = isMultiple
			? Array.isArray(currentValue)
				? currentValue
				: []
			: currentValue ?? '';

		return (
			<Box display='flex' flexDirection='column' width='100%'>
				<SearchableSelect
					name={fieldProperties?.field_name || ''}
					defaultValue={columnDefaultValue}
					placeholder={fieldProperties?.placeholder || ''}
					searchPlaceholder={`Search ${col?.header ?? ''}`}
					onChange={(e: any, selectedRow: any) =>
						handleChange(e, 'select', selectedRow)
					}
					apiType={fieldProperties?.apiType}
					autoFocus={shouldAutoFocus}
					options={col?.editSelectOptions}
					customeFilter={customFilter}
					filterKey={fieldProperties?.filterKey}
					labelKey={fieldProperties?.labelKey}
					valueKey={fieldProperties?.bindingKey}
					attributes={fieldProperties?.attributes}
					isInternal={fieldProperties?.isInternal || !fieldProperties?.apiType}
					multiple={isMultiple}
					value={selectValue}
					getSelectedData={(selectedRow: any) =>
						getSelectedData?.(fieldProperties?.field_name, selectedRow)
					}
					disabled={isFieldDisabled}
				/>
				{fieldError && (
					<Typography color='theme.error.600' type='s5'>
						{fieldError}
					</Typography>
				)}
			</Box>
		);
	}

	if (editVariant === 'date') {
		return (
			<DatePicker
				name={fieldProperties?.field_name || ''}
				defaultValue={columnDefaultValue}
				onChange={(v: any) => handleChange(v, 'date')}
				textFieldProps={{ autoFocus: shouldAutoFocus }}
				error={Boolean(fieldError)}
				helperText={fieldError}
				format={fieldProperties?.dateFormat}
				placeholder={fieldProperties?.placeholder || fieldProperties?.dateFormat}
				value={dateValue}
				disabled={isFieldDisabled}
				minDate={() => resolveDateBoundary(fieldProperties?.min_date)}
				maxDate={() => resolveDateBoundary(fieldProperties?.max_date)}
			/>
		);
	}

	if (editVariant === 'date_time') {
		return (
			<DateTimePicker
				name={fieldProperties?.field_name || ''}
				defaultValue={columnDefaultValue}
				onChange={(v: any) => handleChange(v, 'date')}
				textFieldProps={{ autoFocus: shouldAutoFocus }}
				error={Boolean(fieldError)}
				helperText={fieldError}
				format={fieldProperties?.dateFormat}
				placeholder={fieldProperties?.placeholder || fieldProperties?.dateFormat}
				value={dateValue}
				disabled={isFieldDisabled}
			/>
		);
	}

	if (editVariant === 'file' || editVariant === 'image') {
		return (
			<Box display='flex' flexDirection='column'>
				<UploadMedia
					name={fieldProperties?.field_name || 'table_file'}
					dataPosition={0}
					dataName='table_file'
					label=''
					type={editVariant}
					max_size={10}
					onChange={(v: any) => handleChange(v, 'file')}
					error={Boolean(fieldError)}
					multiple={Boolean(fieldProperties?.is_multiple ?? col?.is_multiple)}
					value={currentValue}
				/>
				{fieldError && (
					<Typography color='theme.error.600' type='s5'>
						{fieldError}
					</Typography>
				)}
			</Box>
		);
	}

	if (editVariant === 'phone') {
		return (
			<PhoneInput
				name={fieldProperties?.field_name || ''}
				default_value={columnDefaultValue}
				onChange={(v: any) => handleChange(v, 'phone')}
				textFieldProps={{ autoFocus: shouldAutoFocus }}
				error={Boolean(fieldError)}
				helperText={fieldError}
				format={fieldProperties?.dateFormat}
				placeholder={fieldProperties?.placeholder || fieldProperties?.dateFormat}
				value={currentValue}
				disabled={isFieldDisabled}
				isInlineEditing
			/>
		);
	}

	if (editVariant === 'checkbox') {
		return (
			<Checkbox
				name={fieldProperties?.field_name || ''}
				defaultChecked={Boolean(columnDefaultValue)}
				onChange={(v: any) => handleChange(v, 'checkbox')}
				error={Boolean(fieldError)}
				helperText={fieldError}
				placeholder={fieldProperties?.placeholder}
				value={Boolean(currentValue)}
				disabled={isFieldDisabled}
			/>
		);
	}

	return null;
};

export default React.memo(InlineEditFields);